'use client';

import { useEffect } from 'react';
import { ThemeProvider as NextThemesProvider, useTheme } from 'next-themes';

import type { ThemeProviderProps } from 'next-themes';

const THEME_COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

/**
 * Syncs the resolved theme to a cookie so the server can render
 * the correct theme class on <html>, preventing hydration mismatches
 * that Sentry Replay detects as replay_hydration_error.
 */
function ThemeCookieSync() {
  const { resolvedTheme } = useTheme();

  useEffect(() => {
    if (resolvedTheme) {
      document.cookie = `theme=${resolvedTheme};path=/;max-age=${THEME_COOKIE_MAX_AGE};SameSite=Lax`;
    }
  }, [resolvedTheme]);

  return null;
}

/**
 * Theme provider wrapping next-themes with sensible defaults.
 * Handles localStorage persistence, SSR, and system preference detection.
 */
export function ThemeProvider({ children, ...props }: ThemeProviderProps) {
  return (
    <NextThemesProvider attribute="class" defaultTheme="system" disableTransitionOnChange enableSystem {...props}>
      <ThemeCookieSync />
      {children}
    </NextThemesProvider>
  );
}

export { useTheme };
